import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import pluralize from "pluralize";

import {
  Alert,
  Box,
  LinearProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { RiInformationLine, RiErrorWarningLine } from "@remixicon/react";

import { ShippingDialogProps } from "../../../types/Shipping";
import BaseShippingDialog from "./BaseShippingDialog";
import { ShipmentValidationError_T } from "../../../utils/shippingManagementValidators";
import useApi from "../../../hooks/useGaneshApi";

const RefreshExportDialog: React.FC<ShippingDialogProps> = (props) => {
  const theme = useTheme();
  const { apiRequest } = useApi();

  const { selectedData } = props;
  const {
    fullySelectedGroups,
    partiallySelectedGroups,
    totalSelectedShipments,
  } = selectedData;

  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState<ShipmentValidationError_T[]>([]);
  const [isComplete, setIsComplete] = useState(false);

  const selectedShipments = [
    ...fullySelectedGroups.flatMap((group) => group.shipments),
    ...partiallySelectedGroups.flatMap((group) => group.shipments),
  ];

  const uniqueShipmentIds = Array.from(
    new Set(
      selectedShipments
        .map((shipment) => shipment.fgs_id)
        .filter((id): id is number => typeof id === "number")
    )
  );

  const shipmentsWithoutFgsId = selectedShipments.filter(
    (shipment) => !shipment.fgs_id
  );

  const { mutateAsync: refreshExport, isPending } = useMutation({
    mutationFn: async () => {
      setProgress(0);
      setErrors([]);
      setIsComplete(false);

      for (let i = 0; i < uniqueShipmentIds.length; i++) {
        const shipmentId = uniqueShipmentIds[i];
        const shipment = selectedShipments.find((s) => s.fgs_id === shipmentId);
        try {
          const response = await apiRequest<{ success: boolean }>(
            "/v1/fulfillments/shipments/exports",
            {
              method: "PUT",
              body: {
                fulfillment_group_shipment_id: shipmentId,
              },
            }
          );

          if (!response.data?.success) {
            throw new Error(response.error || "Unknown error");
          }
        } catch (error) {
          setErrors((prev) => [
            ...prev,
            {
              shipment_id: shipmentId,
              delivery_name: shipment?.delivery_name || "No Name",
              error: error instanceof Error ? error.message : "Unknown error",
            },
          ]);
        }
        setProgress(((i + 1) / uniqueShipmentIds.length) * 100);
      }

      return true;
    },
    onSuccess: () => {
      setIsComplete(true);
    },
    onError: () => {
      setIsComplete(true);
    },
  });

  const handleSubmit = () => {
    refreshExport();
  };

  const isSubmitDisabled =
    isPending ||
    isComplete ||
    selectedShipments.length === 0 ||
    shipmentsWithoutFgsId.length > 0;

  return (
    <BaseShippingDialog
      {...props}
      title="Refresh Export"
      onSubmit={handleSubmit}
      submitText={isPending ? "Refreshing..." : "Refresh"}
      submitDisabled={isSubmitDisabled}
    >
      <Box>
        {selectedShipments.length === 0 && (
          <Alert
            severity="info"
            sx={{ mb: 2, display: "flex", gap: 1 }}
            icon={false}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RiInformationLine
                size={20}
                style={{ color: theme.palette.info.main }}
              />
              <Typography variant="body2">
                Please select at least one delivery with a shipment id to
                proceed.
              </Typography>
            </Box>
          </Alert>
        )}

        {shipmentsWithoutFgsId.length > 0 && (
          <Alert
            severity="error"
            sx={{ mb: 2, display: "flex", gap: 1 }}
            icon={false}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RiErrorWarningLine
                size={20}
                style={{ color: theme.palette.error.main }}
              />
              <Typography variant="body2">
                Cannot refresh: {shipmentsWithoutFgsId.length}{" "}
                {pluralize("delivery", shipmentsWithoutFgsId.length)} missing
                shipment id.
              </Typography>
            </Box>
          </Alert>
        )}

        {selectedShipments.length > 0 && shipmentsWithoutFgsId.length === 0 && (
          <Box>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to refresh the export for{" "}
              {totalSelectedShipments} selected{" "}
              {pluralize("shipment", totalSelectedShipments)}?
            </Typography>

            {(isPending || isComplete) && (
              <Box sx={{ width: "100%", mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Processed: {Math.round(progress)}% (
                  {Math.floor((progress / 100) * uniqueShipmentIds.length)} of{" "}
                  {uniqueShipmentIds.length})
                </Typography>
              </Box>
            )}

            {isComplete && errors.length === 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Successfully refreshed all {uniqueShipmentIds.length}{" "}
                {pluralize("shipment", uniqueShipmentIds.length)}.
              </Alert>
            )}

            {isComplete && errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Failed to refresh {errors.length}{" "}
                  {pluralize("shipment", errors.length)}:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                  {errors.map((error) => (
                    <li key={error.shipment_id}>
                      {error.delivery_name} (ID: {error.shipment_id})
                      {error.error && `: ${error.error}`}
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </BaseShippingDialog>
  );
};

export default RefreshExportDialog;
